import { Injectable } from '@angular/core';
import { BlendPortfolioUtil, PortfolioFlatteningUtil } from '@ddt-web/blending';
import { AppStoreConfig } from '@ddt-web/data-service';
import { BaseHolding, TreeNodeMap, WebUserDataConstants } from '@ddt-web/domain';
import { HoldingsUtil } from '@ddt-web/generic-utils';
import { StoreService } from '@ddt-web/store-service';
import * as has from 'lodash/has';
import { Observable, of } from 'rxjs';
import { map } from 'rxjs/operators';

@Injectable({
	providedIn: 'root'
})
export class ApiParamsService {
	constructor(private storeService: StoreService) { }

	public flattenPortfolios(
		ids: string[],
		storeConfig: AppStoreConfig,
		flattenBlkModels = false,
		useValues = false,
		shouldCheckForInvalidPortfolio = true
	): Observable<Array<Object>> {
		const associatedPortfoliosStoreConfig = storeConfig?.associatedPortfolios;

		if (!associatedPortfoliosStoreConfig) {
			return of([]);
		}
		return this.storeService.selectAll(storeConfig.associatedPortfolios).pipe(
			map((portfolios: any) => {
				return this.flattenAssociatedPortfoliosData(ids, portfolios, flattenBlkModels, useValues, shouldCheckForInvalidPortfolio);
			})
		);
	}

	public flattenAssociatedPortfoliosData(
		portfolioIds: string[],
		associatedPortfolios: TreeNodeMap,
		flattenBlkModels = false,
		useValues = false,
		shouldCheckForInvalidPortfolio = true,
		useValuesAndWeights = false // not to be used for API requests
	): Array<Object> {
		const flattenedPortfolios = portfolioIds.map(id => {
			const portfolio = associatedPortfolios.entities[id];

			if (!shouldCheckForInvalidPortfolio || portfolio) {
				const flattenedHoldings = [];

				let childrenPath = 'children';
				if (has(portfolio, 'positions')) {
					// TODO @Dayal When we type engagements, we should check the type here instead and remove the positions array and just use children.
					// Can have EngagementPortfolio as a type which extends BasePortfolio.
					childrenPath = 'positions';
				}

				PortfolioFlatteningUtil.transformNodeMapToHoldings(associatedPortfolios, portfolio, 100, flattenedHoldings, flattenBlkModels, childrenPath);

				const flattenedPositions = flattenedHoldings.map(holding => this.createPosition(holding, useValues, useValuesAndWeights));

				return {
					id: portfolio.id,
					positions: flattenedPositions
				};
			}
		});

		return shouldCheckForInvalidPortfolio ? flattenedPortfolios.filter(port => !!port) : flattenedPortfolios;
	}

	public flattenACServerResponse(treeNodeMap: TreeNodeMap, portfolioId: string): Object {
		const flattenedHoldings = [];

		PortfolioFlatteningUtil.transformNodeMapToHoldings(treeNodeMap, treeNodeMap[portfolioId], 100, flattenedHoldings, true);
		const flattenedPositions = flattenedHoldings.map(holding => this.createPosition(holding, false, false));

		return {
			id: portfolioId,
			positions: flattenedPositions
		};
	}

	public getAllActiveUniquePortfolioIds(ids: string[], storeConfig: AppStoreConfig): Observable<Array<string>> {
		const associatedPortfoliosStoreConfig = storeConfig?.associatedPortfolios;

		if (!associatedPortfoliosStoreConfig) {
			return of([]);
		}

		return (this.storeService.selectAll(storeConfig.associatedPortfolios).pipe(
			map((associatedPortfolios: any) => {
				return Array.from(ids.reduce((acc: Set<string>, id: string) => {
					const portfolio = associatedPortfolios.entities[id];

					if (portfolio) {
						const childrenPath: string = portfolio.children ? 'children' : 'positions';
						PortfolioFlatteningUtil.getAllUniqueChildPortfolioIds(associatedPortfolios, portfolio, childrenPath, false).forEach(acc.add, acc);

						return acc;
					}

					return acc;
				}, new Set(ids)));
			})
		));
	}

	protected createPosition(holding: BaseHolding, useValues: boolean, useValuesAndWeights: boolean = false): {} {
		const positionParam = {
			...HoldingsUtil.retrieveImportantSecurityIdentifiers(holding, [
				'morningstarFundId'
			]),
			identifier: holding.identifier,
			identifierType: BlendPortfolioUtil.isBlackRockModel(holding) ? WebUserDataConstants.IDENTIFIER_TYPE.MODEL_ID : holding.identifierType,
			error: holding.error // needed for identifying failed securities
		};

		if (useValuesAndWeights) {
			positionParam['value'] = holding.value;
			positionParam['weight'] = holding.weight;
		} else if (useValues && holding.value) {
			positionParam['value'] = holding.value;
		} else {
			positionParam['weight'] = holding.weight;
		}
		return positionParam;
	}
}
